import * as _ from "lodash"
import { IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions } from "../PA.Core/PA.Core.IF.BO"
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic"
import { PAdbContext } from "../PA.Core/PAdbContext"
import { IdentityProvider } from "../PA.Core/Basic/Identity"
import { MessageProvider } from "../PA.Core/Basic/Messages"
import { ProgressProvider } from "../PA.Core/Basic/Progress"
import {
    WB_FundingRuleRow,
    WB_FundingRuleSetRow,
    WB_FundingRuleSetRelationshipRow,
    WB_ProductGroupRow, WB_FundingAccountRow, WB_TPM_EventProductGroupRow, WB_TransactionRow
} from "../PA.Core/PAdbSchemaRows"

import {
    esWB_Funding_Rule,
    esWB_Funding_Rule_Set,
    esWB_Funding_Rule_Set_Relationship, esWB_Transaction, esWB_Transaction_Item
} from "../PA.Core/PAdbSchema"
import { ESRecord } from "../PA.Core/lib/ESRecord"
import { ESDataStore } from "../PA.Core/lib/ESDataStore"
import { PAEnvironment } from "../PA.Core/PAEnvironment"
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver"
import { ASync } from "../PA.Core/Basic/ASync"
import { BehaviorSubject } from "rxjs"
import { FundingRuleBOValidations } from "./FundingRuleBO.Validations"
import { PAusrContext } from "../PA.Core/PAusrContext"
import { RecordUtils } from "../PA.Core/Basic/Records"
import { DateUtils } from "../PA.Core/Basic/Dates"

interface ITargetProductGroups {
    addProductGroup(ppg: WB_ProductGroupRow): void;

    removeProductGroup(ppg: WB_ProductGroupRow): void;

    readonly TargetProductGroupCollectionChanged: BehaviorSubject<ITargetProductGroups>;
}

interface ITargetPartitions {
    addPartition(p: string): void;

    removePartition(p: string): void;

    readonly TargetPartitionCollectionChanged: BehaviorSubject<ITargetPartitions>;
}

interface ITargetFundingAccounts {
    addFundingAccount(fa: WB_FundingAccountRow): void;

    removeFundingAccount(fa: WB_FundingAccountRow): void;

    readonly TargetFundingAccountCollectionChanged: BehaviorSubject<ITargetFundingAccounts>;
}

export class FundingRuleBO implements IBusinessObject, ITargetFundingAccounts, ITargetPartitions {
    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_FundingRule: WB_FundingRuleRow

    usrContext: PAusrContext

    productGroups: WB_ProductGroupRow[]
    fundingAccounts: WB_FundingAccountRow[]
    partitions: string []

    _SetRCollectionChanged: BehaviorSubject<FundingRuleBO>
    _bIsEditable: boolean

    constructor(options?: any) {

        this.CreateOptions = options || {}

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({ id: 0, name: "New Funding Rule" })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = null
        this.LoadProgressProvider = new ProgressProvider("Loading", this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_FundingRule = null

        this.usrContext = PAEnvironment.getValue(PAEnvironment.UserContext)

        this.productGroups = []
        this.fundingAccounts = []
        this.partitions = []

        this._bIsEditable = true

        this._TargetPartitionCollectionChanged = new BehaviorSubject<ITargetPartitions>(undefined)
        this._TargetFundingAccountCollectionChanged = new BehaviorSubject<ITargetFundingAccounts>(undefined)
        this._SetRCollectionChanged = new BehaviorSubject<FundingRuleBO>(undefined)

    }

    get WB_FundingRule(): WB_FundingRuleRow {
        return this.drWB_FundingRule
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        var self = this
        this.LoadProgressProvider.start("Creating Funding Rule")
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {}
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Funding Rule."
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Funding_Rule)
                .then((newFundingRuleRow: WB_FundingRuleRow) => {
                    newFundingRuleRow.setDefaults()
                    self.drWB_FundingRule = newFundingRuleRow
                    self.dbContext.add(esWB_Funding_Rule, newFundingRuleRow)
                    self.updateIdentity()
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    return self._AfterBoLoadCompleteInternal()
                })
                .then(() => {
                    self.LoadProgressProvider.finish()
                    self._bIsLoaded = true
                    self._OnLoadComplete = null
                    resolve(self)

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        })

        return this._OnLoadComplete
    }

    async Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this
        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")")
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = "Fund Rule already loaded."
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(sError)
                    return self
                }
                self.LoadOptions = options || {}

                if ("dbContext" in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext()
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = "Unable to determine dbContext for Fund Rule."
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                if ("RecordResolver" in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver
                    recordResolverLoadPromise = ASync.resolve()
                } else {
                    recordResolver = FundingRuleBO.createRecordResolver({ dbContext: self.dbContext })
                    recordResolverLoadPromise = FundingRuleBO.setupRecordResolver(recordResolver, [anId])
                }

                await recordResolverLoadPromise

                const aFundingRule: any = recordResolver.get(esWB_Funding_Rule, anId)

                if (_.isEmpty(aFundingRule)) {
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception("Id not found.")
                    return self
                }

                self.drWB_FundingRule = aFundingRule
                self.updateIdentity()
                await self._AfterBoLoadCompleteInternal()
                self._bIsLoaded = true
                self._OnLoadComplete = null
                self.LoadProgressProvider.finish()
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                return self
            }
        })()

        return olc
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    get bEditable(): boolean {
        return this._bIsEditable
    }

    set bEditable(value: boolean) {
        this._bIsEditable = value
    }

    async _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this

        self.LoadProgressProvider.setProgressMessage("TPMEventBO.PostLoad", "Processing Loaded Data")
        let results = <WB_TransactionRow[]>await self.dbContext.findAll(esWB_Transaction, {
            where: {
                WB_FUNDINGRULE_ID: self.WB_FundingRule.cif.WB_FUNDINGRULE_ID,
                DELETED_IND: { neq: "1" }
            }
        })

        if (results.length > 0) {
            this._bIsEditable = false
        }

        let sets = _.filter([self.Pending_Rule_Set, self.Active_Rule_Set], r => !_.isEmpty(r))
        if (sets.length == 2) {
            self.resetTargets(self.Active_Rule_Set.cif.WB_FUNDINGRULESET_ID)
            return
        }

        if (sets.length < 2) {
            let newIds: number[] = <number[]>await self.dbContext.getNewIds(esWB_Funding_Rule_Set, 2 - sets.length)

            for (let i = 0; i < newIds.length; i++) {
                let newSet = <WB_FundingRuleSetRow>self.dbContext.createRecord(esWB_Funding_Rule_Set)
                newSet.setDefaults()
                newSet.cif.WB_FUNDINGRULESET_ID = newIds[i]
                newSet.cif.WB_FUNDINGRULE_ID = self.WB_FundingRule.cif.WB_FUNDINGRULE_ID
                newSet.WB_FundingRuleRow = self.WB_FundingRule
                newSet.cif.ACTIVE_IND = "1"
                newSet.cif.WB_AUTHUSR_ID_CREATEDBY = this.usrContext.User.cif.WB_AUTHUSR_ID
                if (sets.length == 1) {
                    if (_.isEmpty(self.Pending_Rule_Set))
                        newSet.cif.RULE_TYPE = "Pending"
                    else
                        newSet.cif.RULE_TYPE = "Active"
                } else {
                    if (i == 0)
                        newSet.cif.RULE_TYPE = "Active"
                    else
                        newSet.cif.RULE_TYPE = "Pending"
                }
            }
            self.resetTargets(self.Active_Rule_Set.cif.WB_FUNDINGRULESET_ID)
            return
        }
    }

    get Pending_Rule_Set(): WB_FundingRuleSetRow {
        const self = this
        return _.first(_.filter(self.WB_FundingRule.Funding_Rule_Sets, s => s.cif.RULE_TYPE == "Pending"))
    }

    get Active_Rule_Set(): WB_FundingRuleSetRow {
        const self = this
        return _.first(_.filter(self.WB_FundingRule.Funding_Rule_Sets, s => s.cif.RULE_TYPE == "Active"))
    }

    get Archived_Rule_Sets(): WB_FundingRuleSetRow[] {
        const self = this
        let rows = _.orderBy(_.filter(self.WB_FundingRule.Funding_Rule_Sets, s => s.isActive && s.cif.RULE_TYPE == "Archived"), ["MODIFIED_DTTS", "WB_FUNDINGRULESET_ID"], ["desc", "desc"])

        _.forEach(rows, (row, idx) => {
            row.Version = DateUtils.convertToSF(DateUtils.convertFromSAPBWTs(row.cif.MODIFIED_DTTS)) + "-" + (rows.length - idx)
        })
        return rows
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this
        this.ValidateMessageProvider.clearMessages()
        this.ValidateProgressProvider.start(
            "Validating " + self.WB_FundingRule.cif.FUNDING_RULE_NAME + "(" + self.WB_FundingRule.cif.FUNDING_RULE_ID + ")"
        )

        self.ValidateOptions = options || {}

        try {
            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext to validate Funding Rule."
                self.ValidateProgressProvider.exception(sError)
                throw sError
            }

            await FundingRuleBOValidations.ValidateFields(self)


            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false
            } else {
                self._bIsValidated = true
            }
            self.ValidateProgressProvider.finish()
            return self

        } catch (err) {
            self._bIsValidated = false
            self.ValidateProgressProvider.exception(err)

        }
        return self

    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this
        this.SaveProgressProvider.start(
            "Saving " + self.WB_FundingRule.cif.FUNDING_RULE_NAME + "(" + self.WB_FundingRule.cif.FUNDING_RULE_ID + ")"
        )

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false
                const sError = "Unable to determine dbContext to Save Funding Rules."
                self.SaveProgressProvider.exception(sError)
                return self
            }

            self.SaveOptions = options || {}

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            if (!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false
                    self.SaveProgressProvider.finish()
                    return self
                }
            }

            await saveFunction(esWB_Funding_Rule, self.WB_FundingRule, {})
            await saveFunction(esWB_Funding_Rule_Set, self.WB_FundingRule.Funding_Rule_Sets, {})

            let rRows = _.flatMap(self.WB_FundingRule.Funding_Rule_Sets, r => {
                return r.Funding_Rule_Set_Relationships
            })
            rRows = _.filter(rRows, r => !(r.isActiveNew && !r.isActive))

            await saveFunction(esWB_Funding_Rule_Set_Relationship, rRows, {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
            } else {
                self._bIsSaved = true
            }

            self.SaveProgressProvider.finish()
        } catch (err) {
            self.SaveProgressProvider.exception(err)

        } finally {

        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }


//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        let self = this

        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start("Deleting " + self.WB_FundingRule.cif.FundingRule_NAME + "(" + self.WB_FundingRule.cif.FundingRule_ID + ")")
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the PeriodType

            // 2. If Valid, mark all the records for deletion.
            self.WB_FundingRule.markDeleted()
            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true
                        self.DeleteProgressProvider.finish()
                        resolve(self)

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
                resolve(self)
            }
        })
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    //</editor-fold>
    private updateIdentity(): void {
        const {
            WB_FUNDRULE_ID,
            FUNDING_RULE_ID,
            FUNDING_RULE_NAME
        } = this.WB_FundingRule.cif
        this.Identity.setAll(WB_FUNDRULE_ID, FUNDING_RULE_ID, FUNDING_RULE_NAME)
        this.LoadProgressProvider.setName(
            "Loading " + this.Identity.name + "(" + this.Identity.id_ui + ")"
        )
        this.Identity.listenToRecordUpdates(this.drWB_FundingRule, "WB_FUNDRULE_ID", "FUNDING_RULE_ID", "FUNDING_RULE_NAME")
        this.Identity.enableRecordWriteBack(this.drWB_FundingRule, "FUNDING_RULE_ID", "FUNDING_RULE_NAME")

    }


    //</editor-fold>

    //<editor-fold desc = "Scope Level operations">
    /*   get TargetProductGroupCollectionChanged(): BehaviorSubject<ITargetProductGroups> {
           return this._TargetProductGroupCollectionChanged
       }

       get TargetProductGroups(): WB_ProductGroupRow[] {
           return this.productGroups
       }

       addProductGroup(ppg: WB_ProductGroupRow): void {
           const self = this
           this.productGroups.push(ppg)
       }

       removeProductGroup(ppg: WB_ProductGroupRow): void {
           const self = this
           this.productGroups.splice(this.productGroups.indexOf(ppg), 1)

       }*/

    _TargetPartitionCollectionChanged: BehaviorSubject<ITargetPartitions>
    get TargetPartitionCollectionChanged(): BehaviorSubject<ITargetPartitions> {
        return this._TargetPartitionCollectionChanged
    }

    get TargetPartitions(): string [] {
        return this.partitions
    }

    addPartition(value: string): void {
        const self = this
        self.partitions.push(value)
        self.TargetPartitionCollectionChanged.next(self)
    }

    removePartition(p: string): void {
        const self = this
        if (self.partitions.indexOf(p) > -1) {
            self.partitions.splice(this.partitions.indexOf(p), 1)
            self.TargetPartitionCollectionChanged.next(self)
        }

    }

    _TargetFundingAccountCollectionChanged: BehaviorSubject<ITargetFundingAccounts>
    get TargetFundingAccountCollectionChanged(): BehaviorSubject<ITargetFundingAccounts> {
        return this._TargetFundingAccountCollectionChanged
    }

    get TargetFundingAccounts(): WB_FundingAccountRow[] {
        return this.fundingAccounts
    }

    addFundingAccount(fa: WB_FundingAccountRow): void {
        const self = this
        self.fundingAccounts.push(fa)
        self.TargetFundingAccountCollectionChanged.next(self)
    }

    removeFundingAccount(fa: WB_FundingAccountRow): void {
        const self = this
        if (self.fundingAccounts.indexOf(fa) > -1) {
            self.fundingAccounts.splice(this.fundingAccounts.indexOf(fa), 1)
            self.TargetFundingAccountCollectionChanged.next(self)
        }
    }

    resetTargets(setWbId: number) {
        const self = this
        let set = _.first(_.filter(self.WB_FundingRule.Funding_Rule_Sets, s => s.cif.WB_FUNDINGRULESET_ID == setWbId))

        self.partitions = _.uniq(_.map(_.filter(set.Funding_Rule_Set_Relationships, r => r.isActive), "PARTITION"))
        self.fundingAccounts = _.uniqBy(_.map(_.filter(set.Funding_Rule_Set_Relationships, r => r.isActive), r => {
            return r.WB_FundingAccountRow
        }), "WB_FUNDINGACCOUNT_ID")
    }

    async syncFundingAccountAllocation(wbSetId: number): Promise<FundingRuleBO> {
        const self = this
        let set: WB_FundingRuleSetRow = _.first(_.filter(self.WB_FundingRule.Funding_Rule_Sets, s => s.isActive && s.cif.WB_FUNDINGRULESET_ID == wbSetId))


        //remove
        _.forEach(set.Funding_Rule_Set_Relationships, r => {
            if (!r.isActive)
                return

            if (!_.includes(self.TargetPartitions, r.cif.PARTITION)) {
                r.markDeleted()
                return
            }

            if (!_.includes(_.map(self.TargetFundingAccounts, "WB_FUNDINGACCOUNT_ID"), r.cif.WB_FUNDINGACCOUNT_ID)) {
                r.markDeleted()
                return
            }
        })

        let TargetPartitions = self.TargetPartitions
        let TargetFundingAccounts = self.TargetFundingAccounts
        let newRows: WB_FundingRuleSetRelationshipRow[] = []
        //add
        for (let p of TargetPartitions) {
            for (let fa of TargetFundingAccounts) {
                if (!_.filter(set.Funding_Rule_Set_Relationships, r => r.isActive && r.cif.PARTITION == p && r.cif.WB_FUNDINGACCOUNT_ID == fa.cif.WB_FUNDINGACCOUNT_ID).length) {
                    let newRow = <WB_FundingRuleSetRelationshipRow>await self.dbContext.createRecordWithKey(esWB_Funding_Rule_Set_Relationship)
                    newRow.setDefaults()
                    newRow.WB_FundingRuleSetRow = set
                    newRow.cif.PARTITION = p
                    newRow.WB_FundingAccountRow = fa
                    newRow.cif.ALLOCATION_PCNT = 0
                    self.dbContext.add(esWB_Funding_Rule_Set_Relationship, newRow)
                }
            }
        }
        return self

    }

    get SetRCollectionChanged(): BehaviorSubject<FundingRuleBO> {
        return this._SetRCollectionChanged
    }

    async copySet(setWbId: number, target: WB_FundingRuleSetRow): Promise<IBusinessObject> {
        const self = this
        const set = _.first(_.filter(self.WB_FundingRule.Funding_Rule_Sets, s => s.isActive && s.cif.WB_FUNDINGRULESET_ID == setWbId))

        let srcRs = _.filter(set.Funding_Rule_Set_Relationships, r => r.isActive && r.cif.WB_FUNDINGRULESET_ID == setWbId)
        let newRIds = <number[]>await self.dbContext.getNewIds(esWB_Funding_Rule_Set_Relationship, srcRs.length)
        for (let i = 0; i < srcRs.length; i++) {
            let newR = <WB_FundingRuleSetRelationshipRow>self.dbContext.createRecord(esWB_Funding_Rule_Set_Relationship)
            RecordUtils.CopyProperties(self.dbContext, esWB_Funding_Rule_Set_Relationship,
                srcRs[i], newR,
                [],
                [],
                {})
            newR.cif.WB_FUNDINGRULESETR_ID = newRIds[i]
            newR.setDefaults()
            newR.WB_FundingRuleSetRow = _.isEmpty(target) ? self.Pending_Rule_Set : target
            newR.WB_FundingAccountRow = srcRs[i].WB_FundingAccountRow
            self.dbContext.add(esWB_Funding_Rule_Set_Relationship, newR)
        }

        return self

    }


    //</editor-fold>

    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {}
        let dbContext: PAdbContext
        if ("dbContext" in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }
        const period_type_options: CreateOptions = {
            standardConditions: {
                [esWB_Funding_Rule]: { DELETED_IND: { neq: "1" } }
            },
            dctIncludeBelongsToNamedRelations: {}
        }

        return new ESRecordResolver(dbContext, period_type_options)
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Funding_Rule, ids)
                .then(() => {
                    resolve()
                })
                .catch((err) => {
                    reject(err)
                })
        })
    }


    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(FundingRuleBO, "FundingRuleBO", "Redistribution Rules")
